import React, { useState } from "react";
import {
  FiMapPin,
  FiShield,
  FiBookOpen,
  FiMenu,
  FiX,
  FiUser,
  FiBriefcase,
  FiChevronDown,
  FiTrendingUp,
  FiCheck,
  FiPhone,
  FiMail,
  FiSearch,
  FiLayers,
  FiHome,
  FiMessageCircle,
  FiActivity,
  FiGrid,
} from "react-icons/fi";
import {
  FiArrowRight,
  FiCheckCircle,
  FiSettings,
  FiLock,
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";

export default function ProTechLuxuryRealty() {
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [projectTab, setProjectTab] = useState("all");
  const [sectorView, setSectorView] = useState("residential");
  const [expandedFaqIndex, setExpandedFaqIndex] = useState(null);

  // Filter Search States
  const [searchLocation, setSearchLocation] = useState("all");
  const [searchStatus, setSearchStatus] = useState("all");

  const basicProjectsLog = [
    {
      id: "pt-realty-01",
      type: "residential",
      title: "The Grand Belvedere Premium Villas",
      city: "Lucknow, UP",
      price: "Starting @ ₹2.40 Cr",
      status: "ready",
      details:
        "Beautiful luxury villas with private backyards, personal open terrace spots, and a massive 5-star club house for your family.",
      image:
        "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?auto=format&fit=crop&w=800&q=80",
    },
    {
      id: "pt-realty-02",
      type: "commercial",
      title: "ProTech Signature Corporate Arcade",
      city: "Lucknow, UP",
      price: "Starting @ ₹4.10 Cr",
      status: "under-construction",
      details:
        "Modern glass-front office building with energy-saving central air conditioning and premium shops on the ground floor.",
      image:
        "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=800&q=80",
    },
    {
      id: "pt-realty-03",
      type: "plots",
      title: "Emerald Enclave Premium Luxury Town-Plots",
      city: "Lucknow, UP",
      price: "Starting @ ₹85 Lakh",
      status: "ready",
      details:
        "Gated luxury plot development featuring wide 40-feet asphalt roads, underground electrical cabling, and immediate legal registry.",
      image:
        "https://images.unsplash.com/photo-1500382017468-9049fed747ef?auto=format&fit=crop&w=800&q=80",
    },
    {
      id: "pt-realty-04",
      type: "petrol-pump",
      title: "Expressway Heavy-Vehicle Fueling Station Complex",
      city: "Jagdishpur, UP",
      price: "Turnkey Setup Provided",
      status: "ready",
      details:
        "PESO-approved industrial fuel layout equipped with canopy structural steel, multi-product dispensers, and high-tensile concrete bays.",
      image:
        "https://images.unsplash.com/photo-1527018601619-a508a2be00cd?auto=format&fit=crop&w=800&q=80",
    },
    {
      id: "pt-realty-05",
      type: "plots",
      title: "Saraswati Royal Farms & Country Plots",
      city: "Lucknow, UP",
      price: "Starting @ ₹1.90 Cr",
      status: "ready",
      details:
        "High-value highway-linked farm plots optimized for luxury weekend cottages, complete with deep tube-well boring and perimeter fencing.",
      image:
        "https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?auto=format&fit=crop&w=800&q=80",
    },
    {
      id: "pt-realty-06",
      type: "petrol-pump",
      title: "Smart Urban Fuel Court & Retail Zone",
      city: "Lucknow, UP",
      price: "Consultation & Built Package",
      status: "under-construction",
      details:
        "Compliance-certified structural development featuring triple-layer underground tank placements and integrated food-court retail structure.",
      image:
        "https://images.unsplash.com/photo-1588011930968-eadac80e6a5a?q=80&w=869&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
    },
  ];

  const sectorDescriptions = {
    residential: {
      headline: "Beautiful Luxury Homes Built For Happy Families",
      tagline: "Live A Premium Lifestyle",
      features: [
        "Beautifully planned home layouts that completely follow all government safety norms and local RERA rules.",
        "Smart interior design with zero space wastage, ensuring your family gets bigger, wider rooms.",
        "Special thick brick walls that block outside city traffic noise so you get total peace inside your home.",
        "Comes with top-class colony amenities like a swimming pool, yoga park, walking tracks, and safe kids' play areas.",
      ],
      highlights: { box1: "7,000+ Happy Families", box2: "100% RERA Approved" },
    },
    commercial: {
      headline: "Top-Class Business Centres & Shopping Markets",
      tagline: "Best Address For Your Business",
      features: [
        "Modern open office designs without blocking pillars, making it very easy to set up your staff cabins.",
        "High-quality external glass walls that stop outside heat while giving a beautiful view of the city.",
        "Heavy-duty electricity setups and 100% power backup to keep your office servers running without a single stop.",
        "Advanced safety design with modern smoke sensors, wide fire escape stairs, and water drainage systems.",
      ],
      highlights: {
        box1: "24+ Million Sq.Ft Built",
        box2: "Top Corporate Tenants",
      },
    },
    plots: {
      headline: "Secured Gated Communities & High-Value Strategic Land Plots",
      tagline: "The Ultimate Asset Foundation",
      features: [
        "100% clear title ownership land banks with fully verified lineage paperwork and immediate registry options.",
        "Demarcated luxury townships featuring state-of-the-art security walls, paved pathways, and green rain gardens.",
        "Pre-approved layout blueprints aligned completely with urban city planning board mastermaps.",
        "Excellent high-growth locations adjacent to developing multi-lane expressways ensuring maximum value growth.",
      ],
      highlights: {
        box1: "1,200+ Premium Acres Devised",
        box2: "Immediate Registry Plots",
      },
    },
    "petrol-pump": {
      headline: "PESO-Approved Turnkey Fueling Stations & Logistics Hubs",
      tagline: "Industrial Fuel Station Infrastructure Specialists",
      features: [
        "End-to-end heavy construction execution from soil testing and underground tank layout stabilization to canopy welding.",
        "Strictly engineered according to National Highway authority access regulations and safety protocols.",
        "Strategic integration of commercial retail stores, automated air bays, and driver restroom buildings.",
        "Precision execution of high-grade industrial vacuum testing for underground fuel pipelines to bypass leakage hazards.",
      ],
      highlights: {
        box1: "45+ Retail Stations Formed",
        box2: "100% PESO Certified Compliance",
      },
    },
  };

  const filteredProjects = basicProjectsLog.filter((project) => {
    const matchTab = projectTab === "all" || project.type === projectTab;
    const matchLoc =
      searchLocation === "all" || project.city.includes(searchLocation);
    const matchStatus =
      searchStatus === "all" || project.status === searchStatus;
    return matchTab && matchLoc && matchStatus;
  });

  return (
    <div className="min-h-screen bg-[#faf9f6] font-sans text-slate-700 scroll-smooth relative">
      <Helmet>
        <title>
          ProTech Construction | Premium Residential, Plots & Petrol Pump
          Infrastructure
        </title>
        <meta
          name="description"
          content="Discover ProTech Construction's premium building standard. Find ultra-luxury residential villas, high-rise apartments, legal land plots, and PESO approved petrol pump development setups."
        />
      </Helmet>

      {/* --- Sticky Luxury Action Sidebar (Desktop Only) --- */}
      <div className="hidden lg:flex flex-col fixed right-0 top-1/3 bg-white border border-stone-200 rounded-l-xl shadow-xl z-40 overflow-hidden text-slate-700 w-12 transition-all hover:w-42 group">
        <a
          href="#talk-to-us"
          className="flex items-center h-12 border-b border-stone-100 px-3.5 hover:bg-[#1e293b] hover:text-white transition-colors gap-3"
        >
          <FiMail
            size={16}
            className="shrink-0 text-slate-500 group-hover:text-white"
          />{" "}
          <span className="text-xs font-bold uppercase tracking-wider opacity-0 group-hover:opacity-100 transition-opacity">
            Enquire Now
          </span>
        </a>
        <a
          href="tel:7457915032"
          className="flex items-center h-12 border-b border-stone-100 px-3.5 hover:bg-[#1e293b] hover:text-white transition-colors gap-3"
        >
          <FiPhone
            size={16}
            className="shrink-0 text-slate-500 group-hover:text-white"
          />{" "}
          <span className="text-xs font-bold uppercase tracking-wider opacity-0 group-hover:opacity-100 transition-opacity">
            Call Us
          </span>
        </a>
        <a
          href="https://wa.me/7457915032"
          target="_blank"
          rel="noreferrer"
          className="flex items-center h-12 px-3.5 hover:bg-emerald-600 hover:text-white transition-colors gap-3"
        >
          <FiMessageCircle
            size={16}
            className="shrink-0 text-emerald-600 group-hover:text-white"
          />{" "}
          <span className="text-xs font-bold uppercase tracking-wider opacity-0 group-hover:opacity-100 transition-opacity">
            WhatsApp Chat
          </span>
        </a>
      </div>

      {/* --- Clean Crisp Premium Navigation Bar --- */}
      <nav className="bg-white/95 backdrop-blur-md border-b border-stone-200/80 sticky top-0 z-50 shadow-sm text-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center gap-2.5">
              <div className="w-10 h-10  rounded flex items-center justify-center text-white font-bold overflow-hidden">
                <img
                  src="/protech.png"
                  alt="Logo"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex flex-col">
                <span className="text-base font-black tracking-widest text-slate-900 uppercase leading-none">
                  PRO<span className="text-emerald-700">TECH</span>
                </span>
                <span className="text-[9px] text-slate-400 font-bold uppercase tracking-widest mt-1">
                  CONSTRUCTION ENTERPRISES
                </span>
              </div>
            </div>

            {/* Desktop Navigation Links */}
            <div className="hidden md:flex items-center gap-6 lg:gap-8 text-xs font-bold uppercase tracking-widest text-slate-600">
              <a
                href="#what-we-do"
                className="hover:text-emerald-700 transition-colors"
              >
                Our Quality
              </a>
              <a
                href="#past-work"
                className="hover:text-emerald-700 transition-colors"
              >
                Our Portfolio
              </a>
              <a
                href="#benefits"
                className="hover:text-emerald-700 transition-colors"
              >
                Why Choose Us
              </a>
              <a
                href="#ask-us"
                className="hover:text-emerald-700 transition-colors"
              >
                Help Desk
              </a>
              <button
                className="px-5 py-2.5 rounded bg-[#1e293b] text-white font-medium tracking-widest hover:bg-slate-900 transition-all flex items-center gap-2 text-xs shadow-sm"
                onClick={() => navigate("/login")}
              >
                <FiLock size={12} className="text-emerald-200" /> PARTNER PORTAL
              </button>
            </div>

            {/* Mobile Menu Icon */}
            <div className="md:hidden">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="text-slate-700 p-2 focus:outline-none hover:text-slate-900"
              >
                {isMobileMenuOpen ? <FiX size={24} /> : <FiMenu size={24} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation List */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-stone-200 px-6 py-4 space-y-3 shadow-lg">
            <a
              href="#what-we-do"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block py-2 font-semibold text-slate-600 border-b border-stone-100 hover:text-emerald-700"
            >
              Our Quality
            </a>
            <a
              href="#past-work"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block py-2 font-semibold text-slate-600 border-b border-stone-100 hover:text-emerald-700"
            >
              Our Portfolio
            </a>
            <a
              href="#benefits"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block py-2 font-semibold text-slate-600 border-b border-stone-100 hover:text-emerald-700"
            >
              Why Choose Us
            </a>
            <a
              href="#ask-us"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block py-2 font-semibold text-slate-600 border-b border-stone-100 hover:text-emerald-700"
            >
              Help Desk
            </a>
            <button
              className="w-full mt-2 px-4 py-3 rounded bg-[#1e293b] text-white font-medium flex items-center justify-center gap-2 text-xs tracking-widest"
              onClick={() => {
                setIsMobileMenuOpen(false);
                navigate("/login");
              }}
            >
              <FiLock size={12} className="text-emerald-200" /> PARTNER PORTAL
            </button>
          </div>
        )}
      </nav>

      {/* --- Soft Aesthetic Hero Section --- */}
      <header className="relative bg-gradient-to-b from-stone-100 via-stone-50 to-[#faf9f6] py-12 lg:py-24 px-4 sm:px-6 text-center lg:text-left overflow-hidden">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 items-center relative z-10">
          <div className="lg:col-span-7">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-800/5 text-slate-700 text-xs font-bold tracking-widest uppercase mb-6 border border-stone-200 mx-auto lg:mx-0">
              <FiCheckCircle size={13} className="text-emerald-700" />{" "}
              RESIDENTIAL • PLOTS • PETROL STATIONS
            </span>

            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-serif text-slate-900 leading-tight mb-6">
              Strategic Real Estate. <br />
              <span className="text-emerald-800 font-sans font-normal italic">
                High-Value Infrastructure.
              </span>
            </h1>

            <p className="text-sm sm:text-base text-slate-600 mb-8 leading-relaxed max-w-xl mx-auto lg:mx-0">
              ProTech Luxury Realty engineers premium housing communities,
              certified commercial land plots, and regulated turnkey petrol pump
              fueling systems that guarantee lifetime value and comprehensive
              legal clearance.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start max-w-sm sm:max-w-none mx-auto">
              <a
                href="#past-work"
                className="px-7 py-3.5 bg-slate-800 text-white rounded font-medium text-xs uppercase tracking-widest hover:bg-slate-900 transition-all shadow-sm text-center"
              >
                View Properties & Projects
              </a>
              <a
                href="#talk-to-us"
                className="px-7 py-3.5 bg-white text-slate-700 border border-stone-300 rounded font-bold text-xs uppercase tracking-widest hover:bg-stone-50 transition-all text-center flex items-center justify-center gap-2 shadow-xs"
              >
                Book Free Consultation{" "}
                <FiArrowRight className="text-emerald-700" />
              </a>
            </div>
          </div>

          {/* Luxury Showcase Display Block */}
          <div className="lg:col-span-5 px-2 sm:px-0 relative">
            <div className="absolute inset-0 bg-stone-200/50 rounded-2xl filter blur-xl"></div>
            <img
              // src="https://images.unsplash.com/photo-1541888946425-d81bb19240f5?auto=format&fit=crop&w=800&q=80"
              src="https://images.unsplash.com/photo-1599707254554-027aeb4deacd?q=80&w=871&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
              alt="High grade real estate land development and civil asset layout planning"
              className="rounded-2xl shadow-xl border border-stone-200 w-full object-cover h-64 sm:h-72 lg:h-96 relative z-10"
            />
            <div className="absolute -bottom-4 -left-4 bg-white border border-stone-200 p-4 rounded-xl shadow-md z-20 hidden sm:block">
              <p className="text-xs text-emerald-800 font-bold uppercase tracking-widest">
                Featured Execution
              </p>
              <p className="text-sm font-bold text-slate-800 mt-0.5">
                Turnkey Commercial Frameworks
              </p>
            </div>
          </div>
        </div>

        {/* --- FLOATING PROPERTY MATCH FINDER --- */}
        <div className="max-w-5xl mx-auto mt-12 lg:mt-16 bg-white text-slate-800 rounded-xl shadow-lg border border-stone-200/80 p-4 sm:p-6 relative z-20 mx-4 sm:mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-center">
            {/* Filter 1: Market Type */}
            <div className="space-y-1.5 text-left border-b sm:border-b-0 sm:border-r border-stone-200 pb-3 sm:pb-0 sm:pr-4">
              <label className="text-[10px] font-black uppercase text-stone-400 tracking-wider flex items-center gap-1.5">
                <FiHome className="text-emerald-700" /> Project Domain
              </label>
              <select
                value={projectTab}
                onChange={(e) => setProjectTab(e.target.value)}
                className="w-full bg-transparent text-sm font-bold text-slate-700 outline-none cursor-pointer focus:text-emerald-800"
              >
                <option value="all">
                  All Domains (Villas, Plots, Fuel Courts)
                </option>
                <option value="residential">Luxury Homes & Villas</option>
                <option value="commercial">
                  Corporate Offices & Complexes
                </option>
                <option value="plots">Premium Gated Plots</option>
                <option value="petrol-pump">Turnkey Petrol Pumps</option>
              </select>
            </div>

            {/* Filter 2: Cities */}
            <div className="space-y-1.5 text-left border-b sm:border-b-0 sm:border-r border-stone-200 pb-3 sm:pb-0 sm:pr-4">
              <label className="text-[10px] font-black uppercase text-stone-400 tracking-wider flex items-center gap-1.5">
                <FiMapPin className="text-emerald-700" /> Targeted City
              </label>
              <select
                value={searchLocation}
                onChange={(e) => setSearchLocation(e.target.value)}
                className="w-full bg-transparent text-sm font-bold text-slate-700 outline-none cursor-pointer focus:text-emerald-800"
              >
                <option value="all">All Active Locations</option>
                <option value="Lucknow">Lucknow, UP</option>
                <option value="Amethi">Amethi, UP</option>
                <option value="Jagdishpur">Jagdishpur, UP</option>
              </select>
            </div>

            {/* Filter 3: Status */}
            <div className="space-y-1.5 text-left pb-2 sm:pb-0">
              <label className="text-[10px] font-black uppercase text-stone-400 tracking-wider flex items-center gap-1.5">
                <FiSettings className="text-emerald-700" /> Development Phase
              </label>
              <select
                value={searchStatus}
                onChange={(e) => setSearchStatus(e.target.value)}
                className="w-full bg-transparent text-sm font-bold text-slate-700 outline-none cursor-pointer focus:text-emerald-800"
              >
                <option value="all">All Development Phases</option>
                <option value="ready">Completed / Ready to Assign</option>
                <option value="under-construction">
                  Active Construction / Under Work
                </option>
              </select>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-stone-100 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 text-xs text-stone-500">
            <span>
              Found{" "}
              <strong className="text-slate-800">
                {filteredProjects.length}
              </strong>{" "}
              matching strategic assets
            </span>
            <a
              href="#past-work"
              className="font-bold text-emerald-800 hover:text-emerald-900 flex items-center gap-1 uppercase tracking-wider text-[11px]"
            >
              View Screened Listings <FiArrowRight size={12} />
            </a>
          </div>
        </div>
      </header>

      {/* --- Premium Trust Strip --- */}
      <section className="px-4 sm:px-6 py-8 bg-white border-b border-stone-200 shadow-xs relative z-10">
        <div className="max-w-6xl mx-auto grid grid-cols-2 lg:grid-cols-4 gap-6 text-center">
          <SimpleStat digit="15+ Years" tag="Infrastructure Standing" />
          <MetricBlockSimple
            blockTitle="Compliance Assurance"
            blockValue="PESO & RERA Approved"
          />
          <SimpleStat digit="24M+ Sq.Ft" tag="Total Area Managed" />
          <MetricBlockSimple
            blockTitle="Title Clearance"
            blockValue="100% Legal Guarantee"
          />
        </div>
      </section>

      {/* --- Elegant Segment Tabs --- */}
      <section
        id="what-we-do"
        className="px-4 sm:px-6 py-16 lg:py-24 bg-[#faf9f6]"
      >
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-serif text-slate-900 tracking-wide mb-3">
            Core Construction & Acquisition Domains
          </h2>
          <p className="text-stone-500 max-w-md mx-auto text-xs sm:text-sm mb-8 leading-relaxed">
            Select an infrastructure tab to review technical specifications,
            development safety rules, and acquisition policies.
          </p>

          {/* Choice Selection Bars */}
          <div className="flex flex-wrap justify-center gap-2 sm:gap-4 mb-8">
            <button
              onClick={() => setSectorView("residential")}
              className={`px-4 sm:px-6 py-2.5 sm:py-3 rounded text-[11px] sm:text-xs font-bold uppercase tracking-widest transition-all flex items-center gap-2 ${
                sectorView === "residential"
                  ? "bg-slate-800 text-white shadow-md border border-slate-800"
                  : "bg-white text-slate-600 border border-stone-200 hover:bg-stone-100"
              }`}
            >
              <FiHome size={14} /> Luxury Homes
            </button>
            <button
              onClick={() => setSectorView("commercial")}
              className={`px-4 sm:px-6 py-2.5 sm:py-3 rounded text-[11px] sm:text-xs font-bold uppercase tracking-widest transition-all flex items-center gap-2 ${
                sectorView === "commercial"
                  ? "bg-slate-800 text-white shadow-md border border-slate-800"
                  : "bg-white text-slate-600 border border-stone-200 hover:bg-stone-100"
              }`}
            >
              <FiBriefcase size={14} /> Offices
            </button>
            <button
              onClick={() => setSectorView("plots")}
              className={`px-4 sm:px-6 py-2.5 sm:py-3 rounded text-[11px] sm:text-xs font-bold uppercase tracking-widest transition-all flex items-center gap-2 ${
                sectorView === "plots"
                  ? "bg-slate-800 text-white shadow-md border border-slate-800"
                  : "bg-white text-slate-600 border border-stone-200 hover:bg-stone-100"
              }`}
            >
              <FiGrid size={14} /> Premium Plots
            </button>
            <button
              onClick={() => setSectorView("petrol-pump")}
              className={`px-4 sm:px-6 py-2.5 sm:py-3 rounded text-[11px] sm:text-xs font-bold uppercase tracking-widest transition-all flex items-center gap-2 ${
                sectorView === "petrol-pump"
                  ? "bg-slate-800 text-white shadow-md border border-slate-800"
                  : "bg-white text-slate-600 border border-stone-200 hover:bg-stone-100"
              }`}
            >
              <FiActivity size={14} /> Petrol Pumps
            </button>
          </div>

          {/* Integrated Multi-column Info Block */}
          <div className="bg-white rounded-xl border border-stone-200 text-left grid lg:grid-cols-12 gap-0 items-stretch shadow-sm overflow-hidden">
            <div className="lg:col-span-7 p-6 sm:p-10 space-y-4">
              <span className="text-emerald-800 text-xs font-bold uppercase tracking-widest block">
                {sectorDescriptions[sectorView].tagline}
              </span>
              <h3 className="text-xl sm:text-2xl font-bold text-slate-800 tracking-tight">
                {sectorDescriptions[sectorView].headline}
              </h3>
              <div className="h-0.5 bg-emerald-700/60 w-16 my-2 rounded-full"></div>
              <ul className="space-y-3.5 pt-3">
                {sectorDescriptions[sectorView].features.map((point, index) => (
                  <li
                    key={index}
                    className="flex items-start gap-3 text-slate-600 text-xs sm:text-sm leading-relaxed"
                  >
                    <div className="p-0.5 rounded-full bg-stone-100 text-emerald-800 mt-0.5 shrink-0">
                      <FiCheck size={14} />
                    </div>
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Stats Graphic Right block */}
            <div className="lg:col-span-5 bg-stone-100/60 border-t lg:border-t-0 lg:border-l border-stone-200 p-8 text-center space-y-6 h-full flex flex-col justify-center">
              <div>
                <p className="text-3xl font-light text-slate-900 tracking-tight">
                  {sectorDescriptions[sectorView].highlights.box1}
                </p>
                <p className="text-[10px] uppercase font-bold text-slate-400 tracking-widest mt-1.5">
                  Verified Matrix
                </p>
              </div>
              <div className="h-px bg-stone-200 w-1/3 mx-auto"></div>
              <div>
                <p className="text-xs font-bold text-slate-700 uppercase tracking-widest border border-stone-300 px-3 py-1.5 inline-block rounded bg-white shadow-xs">
                  {sectorDescriptions[sectorView].highlights.box2}
                </p>
                <p className="text-[10px] uppercase font-bold text-slate-400 tracking-widest mt-2">
                  Regulatory Clearance Status
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* --- Dynamic Search-Linked Portfolio Grid --- */}
      <section
        id="past-work"
        className="px-4 sm:px-6 py-16 lg:py-24 bg-white border-t border-b border-stone-200/80"
      >
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-serif text-slate-900 tracking-wide mb-3">
            Our Property & Infrastructure Portfolio
          </h2>
          <p className="text-stone-500 text-xs sm:text-sm max-w-md mx-auto mb-12 leading-relaxed">
            Real estate assets, masterplots, and specialized commercial setups
            matched according to your filters.
          </p>

          {/* Fallback Screen for Empty Search Array */}
          {filteredProjects.length === 0 ? (
            <div className="p-12 text-center bg-stone-50 border border-dashed border-stone-300 rounded-xl max-w-xl mx-auto">
              <p className="text-sm font-bold text-slate-600">
                No projects or land configurations match your choices.
              </p>
              <button
                onClick={() => {
                  setProjectTab("all");
                  setSearchLocation("all");
                  setSearchStatus("all");
                }}
                className="mt-3 text-xs font-black uppercase text-emerald-800 tracking-wider hover:underline"
              >
                Reset System Filters
              </button>
            </div>
          ) : (
            /* Luxury Collection Card Stack */
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 max-w-sm md:max-w-none mx-auto">
              {filteredProjects.map((project) => (
                <div
                  key={project.id}
                  className="bg-white rounded-xl border border-stone-200 overflow-hidden shadow-xs hover:shadow-md transition-all duration-300 flex flex-col justify-between group"
                >
                  <div>
                    {/* Top Aesthetic Image Layer */}
                    <div className="h-48 bg-stone-100 relative overflow-hidden">
                      <img
                        src={project.image}
                        alt={project.title}
                        className="w-full h-full object-cover transform group-hover:scale-102 transition-transform duration-500"
                      />
                      <span className="absolute top-3 left-3 text-[9px] font-bold uppercase px-2 py-1 bg-white/95 text-slate-800 rounded tracking-widest z-20 border border-stone-200 shadow-xs">
                        {project.type === "residential" && "Luxury Home"}
                        {project.type === "commercial" && "Corporate Block"}
                        {project.type === "plots" && "Strategic Plot"}
                        {project.type === "petrol-pump" && "PESO Fuel Unit"}
                      </span>
                      <span className="absolute bottom-3 right-3 text-[10px] font-bold uppercase px-2 py-0.5 bg-slate-800 text-white rounded tracking-wider z-20">
                        {project.status === "ready"
                          ? "Ready / Assigned"
                          : "Under Active Build"}
                      </span>
                    </div>

                    <div className="p-5 sm:p-6 text-left">
                      <div className="flex justify-between items-center mb-2 gap-2">
                        <span className="text-xs text-emerald-800 font-bold flex items-center gap-1 uppercase tracking-wider truncate">
                          <FiMapPin size={12} className="shrink-0" />{" "}
                          {project.city}
                        </span>
                        <span className="text-xs font-bold text-slate-900 bg-stone-100 px-2 py-1 rounded shrink-0">
                          {project.price}
                        </span>
                      </div>
                      <h3 className="font-bold text-slate-800 text-base mb-2 tracking-tight group-hover:text-emerald-800 transition-colors uppercase text-sm sm:text-base">
                        {project.title}
                      </h3>
                      <p className="text-stone-500 text-xs leading-relaxed">
                        {project.details}
                      </p>
                    </div>
                  </div>

                  <div className="p-5 sm:p-6 pt-0 border-t border-stone-100 flex items-center justify-between mt-2">
                    <span className="text-[10px] font-bold text-stone-400 uppercase tracking-widest flex items-center gap-1">
                      Verified Layout{" "}
                      <FiCheckCircle className="text-emerald-600" />
                    </span>
                    <a
                      href="#talk-to-us"
                      className="text-xs font-bold text-slate-800 uppercase tracking-wider hover:text-emerald-800 flex items-center gap-1"
                    >
                      Request Details{" "}
                      <FiArrowRight size={12} className="text-emerald-700" />
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* --- Core Values Strengths Grid --- */}
      <section
        id="benefits"
        className="px-4 sm:px-6 py-16 lg:py-24 bg-stone-50"
      >
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl font-serif text-slate-900 tracking-wide mb-2">
              Why Institutional Buyers Trust ProTech
            </h2>
            <p className="text-stone-500 text-xs sm:text-sm">
              Complete physical safety, title clearance, and compliance
              engineering.
            </p>
            <div className="h-0.5 w-16 bg-emerald-700/60 mx-auto rounded-full mt-4"></div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 max-w-md sm:max-w-none mx-auto">
            <SimpleBenefitCard
              title="All-In-One Acquisition"
              text="We direct everything directly—from land bank screening, zoning changes, and material verification to automated structural checks."
            />
            <SimpleBenefitCard
              title="Acoustic & Vibration Design"
              text="Residential buildings use advanced thick-wall density setups designed specifically to offset heavy highway and traffic noise."
            />
            <SimpleBenefitCard
              title="Industrial Fuel Station Safety"
              text="Petrol pump spaces are engineered with standard leak-proof pipelines, heavy copper grounding networks, and automated vacuum shutoffs."
            />
            <SimpleBenefitCard
              title="Eco-Managed Infrastructure"
              text="Our developments feature certified storm water retention loops, multi-kilowatt solar canopy alignments, and natural cross-air pathways."
            />
            <SimpleBenefitCard
              title="Rigid Compliance Oversight"
              text="We process complete layout maps, state explosive department licenses, and RERA certifications independently, ensuring zero future legal risks."
            />
            <SimpleBenefitCard
              title="Transparent Execution Costs"
              text="Advanced structural blueprint testing avoids building modifications, concrete raw material wastage, and logistical pricing inflation."
            />
          </div>
        </div>
      </section>

      {/* --- Elegant FAQ Accordion Sheet --- */}
      <section id="ask-us" className="px-4 sm:px-6 py-16 lg:py-24 bg-white">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-serif text-slate-900 tracking-wide">
              Frequently Asked Questions
            </h2>
            <p className="text-stone-400 text-xs mt-2">
              Clear data regarding land registries, commercial fuel pump codes,
              and allotment terms.
            </p>
          </div>

          <div className="space-y-4">
            <PlainFaqBlock
              ask="Do you provide turnkey support for petrol pump explosive department approvals?"
              reply="Yes. Our dedicated commercial infrastructure wing manages the layout drafting, NHAI access clearances, state pollution board NOCs, and final PESO certification pipelines systematically."
              isCurrent={expandedFaqIndex === 0}
              onPress={() =>
                setExpandedFaqIndex(expandedFaqIndex === 0 ? null : 0)
              }
            />
            <PlainFaqBlock
              ask="Are your investment land plots fully mutated and legal?"
              reply="Every plot development layout marketed by ProTech features clear title certification, pre-mutated land records, and complete clearance from regional urban development boards."
              isCurrent={expandedFaqIndex === 1}
              onPress={() =>
                setExpandedFaqIndex(expandedFaqIndex === 1 ? null : 1)
              }
            />
            <PlainFaqBlock
              ask="Can clients audit structural certificates and steel grades online?"
              reply="Yes. Transparency is a baseline rule. Metallurgical testing sheets, concrete compressive metrics, and land zoning documents are updated directly to your secured Partner Portal."
              isCurrent={expandedFaqIndex === 2}
              onPress={() =>
                setExpandedFaqIndex(expandedFaqIndex === 2 ? null : 2)
              }
            />
          </div>
        </div>
      </section>

      {/* --- Premium Booking Conversion Form --- */}
      <section
        id="talk-to-us"
        className="px-4 py-16 lg:py-24 bg-stone-50 border-t border-stone-200"
      >
        <div className="max-w-5xl mx-auto">
          <div className="bg-white rounded-2xl border border-stone-200 shadow-md overflow-hidden grid lg:grid-cols-12">
            {/* Soft Contact Card Left */}
            <div className="lg:col-span-5 bg-slate-800 p-6 sm:p-8 text-white flex flex-col justify-between relative">
              <div className="relative z-10">
                <h2 className="text-xl font-serif tracking-wider mb-4 text-emerald-100">
                  Request Asset Portfolio
                </h2>
                <p className="text-stone-300 text-xs sm:text-sm leading-relaxed mb-6">
                  Provide your requirements below. Our technical evaluation desk
                  will contact you within 24 working hours to provide design
                  layouts, clear-title verifications, and compliance logs.
                </p>
              </div>
              <div className="space-y-4 pt-6 border-t border-slate-700 text-xs font-medium text-stone-200 relative z-10 tracking-wider uppercase">
                <div className="flex items-center gap-3">
                  <FiPhone className="text-emerald-200" size={14} /> +91
                  7457915032
                </div>
                <div className="flex items-center gap-3">
                  <FiMail className="text-emerald-200" size={14} />{" "}
                  protechcon6500@gmail.com
                </div>
                <div className="flex items-center gap-3">
                  <FiMapPin className="text-emerald-200" size={14} /> Lucknow,
                  UP, India
                </div>
              </div>
            </div>

            {/* Light High-End Input Card Right */}
            <form
              action="https://api.web3forms.com/submit"
              method="POST"
              className="lg:col-span-7 p-6 sm:p-10 space-y-5 bg-white"
            >
              <input
                type="hidden"
                name="access_key"
                value="YOUR_WEB3FORMS_ACCESS_KEY"
              />
              <input
                type="hidden"
                name="subject"
                value="New Infrastructure Request - ProTech"
              />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <LightFieldWrapper topic="Your Full Name">
                  <input
                    required
                    name="client_name"
                    className="w-full bg-stone-50 border border-stone-200 rounded p-3 text-xs font-semibold text-slate-800 focus:border-slate-400 outline-none transition-colors"
                    placeholder="e.g. Anand Sharma"
                  />
                </LightFieldWrapper>

                <LightFieldWrapper topic="Your Mobile Number">
                  <input
                    required
                    type="tel"
                    name="client_phone"
                    className="w-full bg-stone-50 border border-stone-200 rounded p-3 text-xs font-semibold text-slate-800 focus:border-slate-400 outline-none transition-colors"
                    placeholder="10-digit phone number"
                  />
                </LightFieldWrapper>
              </div>

              <LightFieldWrapper topic="Asset Domain Classification">
                <select
                  name="building_choice"
                  className="w-full bg-stone-50 border border-stone-200 rounded p-3 text-xs text-slate-700 focus:border-slate-400 outline-none font-bold cursor-pointer transition-colors"
                >
                  <option>Luxury Villa Community Setup</option>
                  <option>Premium Smart Apartment / Highrise</option>
                  <option>Turnkey Petrol Pump Construction</option>
                  <option>Gated Residential / Commercial Plots</option>
                  <option>Commercial Corporate Hub Spaces</option>
                </select>
              </LightFieldWrapper>

              <LightFieldWrapper topic="Preferred Region / Location">
                <input
                  required
                  name="site_location"
                  placeholder="e.g. Gomti Nagar, Noida Sector 150"
                  className="w-full bg-stone-50 border border-stone-200 rounded p-3 text-xs font-semibold text-slate-800 focus:border-slate-400 outline-none transition-colors"
                />
              </LightFieldWrapper>

              <LightFieldWrapper topic="Detailed Project Metrics (Optional)">
                <textarea
                  name="extra_notes"
                  rows="3"
                  placeholder="e.g. Seeking roadside commercial plots for corporate retail use or specific highway petrol pump tank configurations..."
                  className="w-full bg-stone-50 border border-stone-200 rounded p-3 text-xs font-semibold text-slate-800 focus:border-slate-400 outline-none transition-colors"
                />
              </LightFieldWrapper>

              <button
                type="submit"
                className="w-full py-3.5 bg-slate-800 text-white rounded font-medium uppercase tracking-widest text-xs hover:bg-slate-900 transition-all shadow-sm active:scale-[0.99]"
              >
                File Requirements Form
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* --- Accessible High-End Footer Sheet --- */}
      <footer className="bg-slate-900 text-slate-400 py-12 sm:py-16 px-4 sm:px-6 relative z-10">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-10 sm:gap-12">
          <div className="space-y-4 text-center md:text-left">
            <h5 className="font-bold text-white uppercase tracking-widest text-xs">
              ProTech Luxury Realty & Infra
            </h5>
            <p className="text-xs text-slate-400 leading-relaxed max-w-xs mx-auto md:mx-0">
              Approved real estate firm engineering compliant residential
              societies, commercial investment lands, and highly regulated fuel
              distribution infrastructure.
            </p>
          </div>

          <div className="text-center md:text-left border-y border-slate-800 md:border-none py-6 md:py-0 space-y-2">
            <h5 className="font-bold text-white tracking-widest uppercase text-xs">
              Head Office
            </h5>
            <p className="text-xs text-slate-400">
              Lucknow, Uttar Pradesh, India
            </p>
            <p className="text-emerald-200 font-medium text-xs tracking-wider mt-2">
              7457915032
            </p>
          </div>

          <div className="text-center md:text-right flex flex-col md:items-end space-y-3">
            <h5 className="font-bold text-white tracking-widest uppercase text-xs">
              Technical Links
            </h5>
            <ul className="text-xs text-slate-400 space-y-2.5 font-semibold">
              <li>
                <button
                  onClick={() => navigate("/past-work")}
                  className="hover:text-white transition-colors"
                >
                  Residential Portfolios
                </button>
              </li>
              <li>
                <button
                  onClick={() => navigate("/plots-portfolio")}
                  className="hover:text-white transition-colors"
                >
                  Township Plot Blueprints
                </button>
              </li>
              <li>
                <button
                  onClick={() => navigate("/fuel-infra")}
                  className="hover:text-white transition-colors"
                >
                  Petrol Pump Compliance Logs
                </button>
              </li>
            </ul>
          </div>
        </div>

        {/* Legal Disclaimer & RERA Badge Lines */}
        <div className="max-w-7xl mx-auto mt-12 sm:mt-16 pt-8 border-t border-slate-800 text-center md:text-left space-y-4">
          <p className="text-[10px] text-slate-500 leading-relaxed max-w-4xl">
            Disclaimer: Architectural render displays, layout blueprints,
            landscape parameters, and construction pricing models shown on this
            domain represent artistic previews. Real execution models depend on
            regional planning guidelines and licensing. Verify respective state
            RERA maps and PESO clearances before transaction executions.
          </p>
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 pt-2">
            <div className="text-slate-500 text-[10px] tracking-wider font-semibold">
              © 2026 ProTech Luxury Realty. All Rights Reserved.
            </div>

            <div className="flex items-center gap-4 sm:gap-6 text-[10px] text-slate-400 font-bold tracking-widest uppercase">
              <button
                onClick={() => navigate("/privacy")}
                className="hover:text-white transition-colors"
              >
                Privacy Policy
              </button>
              <div className="w-1 h-1 bg-slate-800 rounded-full hidden md:block" />
              <button
                onClick={() => navigate("/terms")}
                className="hover:text-white transition-colors"
              >
                Terms of Service
              </button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

// Sub-components used for layout mapping
function SimpleBenefitCard({ title, text }) {
  return (
    <div className="p-6 bg-white rounded-xl border border-stone-200 text-left hover:bg-white hover:shadow-md transition-all duration-300">
      <div className="w-9 h-9 rounded bg-stone-100 text-emerald-800 flex items-center justify-center font-bold mb-4">
        <FiSettings size={16} />
      </div>
      <h3 className="text-xs font-bold mb-1.5 text-slate-800 uppercase tracking-wider">
        {title}
      </h3>
      <p className="text-stone-500 leading-relaxed text-xs">{text}</p>
    </div>
  );
}

function SimpleStat({ digit, tag }) {
  return (
    <div className="p-1">
      <h4 className="text-xl sm:text-2xl font-light text-slate-900 tracking-tight">
        {digit}
      </h4>
      <p className="text-stone-400 text-[10px] font-bold uppercase tracking-widest mt-1">
        {tag}
      </p>
    </div>
  );
}

function MetricBlockSimple({ blockTitle, blockValue }) {
  return (
    <div className="p-1">
      <h4 className="text-xl sm:text-2xl font-medium text-emerald-800 tracking-tight">
        {blockValue}
      </h4>
      <p className="text-stone-400 text-[10px] font-bold uppercase tracking-widest mt-1">
        {blockTitle}
      </p>
    </div>
  );
}

function LightFieldWrapper({ topic, children }) {
  return (
    <div className="space-y-1.5 text-left">
      <label className="text-[10px] font-bold text-stone-400 tracking-widest uppercase flex items-center gap-1">
        {children.props && children.props.name === "client_name" ? (
          <FiUser className="text-emerald-800" />
        ) : (
          <FiSettings className="text-emerald-800" />
        )}{" "}
        {topic}
      </label>
      {children}
    </div>
  );
}

function PlainFaqBlock({ ask, reply, isCurrent, onPress }) {
  return (
    <div className="bg-stone-50 rounded-lg border border-stone-200 overflow-hidden transition-all duration-200">
      <button
        onClick={onPress}
        className="w-full p-4 text-left font-bold text-slate-700 flex justify-between items-center hover:bg-stone-100/60 transition-colors text-xs sm:text-sm"
      >
        <span className="uppercase tracking-tight text-xs">{ask}</span>
        <FiChevronDown
          className={`transform transition-transform text-stone-400 ${isCurrent ? "rotate-180 text-emerald-800" : ""}`}
          size={16}
        />
      </button>
      {isCurrent && (
        <div className="p-4 text-xs text-slate-600 border-t border-stone-200 bg-white leading-relaxed">
          {reply}
        </div>
      )}
    </div>
  );
}
